import { useCallback, useEffect, useRef, useState } from 'react';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const AUTOSAVE_MS = 800;

export interface DocumentAutosave<T> {
  content: T;
  update: (updater: (c: T) => T) => void;
  /** Сохраняет немедленно. Возвращает true, если сервер принял изменения. */
  saveNow: () => Promise<boolean>;
  saveState: SaveState;
  /** Локализованная причина неудачного сохранения (или null). */
  saveErrorMessage: string | null;
}

export interface DocumentAutosaveOptions<T> {
  /** Id документа. Его смена сбрасывает состояние и дожимает отложенный сейв. */
  docId: string;
  initial: T;
  readOnly?: boolean;
  /** Сохранение на сервер. Ошибку не глотает — её разбирает `errorMessage`. */
  save: (docId: string, value: T) => Promise<unknown>;
  /** Локализованный текст ошибки сохранения. */
  errorMessage: (error: unknown) => string;
  /**
   * Досылка отложенного сохранения после размонтирования: бейджа уже нет, и о
   * неудаче надо сообщить самому — тостом, живущим вне размонтированного дерева.
   */
  onDetachedFailure: (error: unknown) => void;
  /** Что сделать после успешной досылки (обычно — инвалидация кэша). */
  onDetachedSuccess?: () => void;
}

/**
 * Владеет редактируемым содержимым документа и его отложенным сохранением.
 * Держит всю возню с таймерами и рефами вне вида, чтобы редактор оставался
 * презентационным.
 *
 * Ничего не знает ни про эссе, ни про квартальный отчёт: форму содержимого
 * задаёт вызывающий, сохранение передаётся функцией. Сброс по смене `docId`
 * (вместе со снятием запланированного сейва) не даёт отложенному сохранению
 * уехать в другой документ, когда тот же экземпляр редактора переиспользуют с
 * новым id — так делает страница управления.
 */
export function useDocumentAutosave<T>(opts: DocumentAutosaveOptions<T>): DocumentAutosave<T> {
  const { docId, initial, readOnly, save, errorMessage, onDetachedFailure, onDetachedSuccess } =
    opts;

  const [content, setContent] = useState<T>(initial);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [saveErrorMessage, setSaveErrorMessage] = useState<string | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Актуальное содержимое для saveNow: state внутри колбэка может быть устаревшим.
  const contentRef = useRef<T>(initial);
  // Содержимое, для которого запланирован, но ещё не выполненный автосейв.
  const pendingRef = useRef<T | null>(null);
  // Id, к которому относится pendingRef. Обновляется в эффекте, поэтому в его
  // cleanup ещё виден предыдущий id.
  const docIdRef = useRef(docId);

  // Колбэки в рефах: они пересобираются на каждый рендер вызывающего, а эффект
  // сброса должен зависеть только от docId.
  const saveRef = useRef(save);
  const failureRef = useRef(onDetachedFailure);
  const successRef = useRef(onDetachedSuccess);
  saveRef.current = save;
  failureRef.current = onDetachedFailure;
  successRef.current = onDetachedSuccess;

  const clearTimer = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const flushPending = useCallback((id: string, pending: T) => {
    saveRef
      .current(id, pending)
      .then(() => successRef.current?.())
      .catch((error) => failureRef.current(error));
  }, []);

  // Сброс при переходе на другой документ. Cleanup этого же эффекта срабатывает
  // и при размонтировании, и при смене id — в обоих случаях дожимаем отложенный
  // сейв.
  useEffect(() => {
    clearTimer();
    contentRef.current = initial;
    pendingRef.current = null;
    docIdRef.current = docId;
    setContent(initial);
    setSaveState('idle');
    setSaveErrorMessage(null);
    return () => {
      clearTimer();
      const pending = pendingRef.current;
      pendingRef.current = null;
      if (pending) flushPending(docIdRef.current, pending);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docId]);

  const persist = useCallback(
    async (next: T): Promise<boolean> => {
      pendingRef.current = null;
      setSaveState('saving');
      try {
        await saveRef.current(docId, next);
        setSaveState('saved');
        setSaveErrorMessage(null);
        return true;
      } catch (error) {
        setSaveState('error');
        setSaveErrorMessage(errorMessage(error));
        return false;
      }
    },
    [docId, errorMessage],
  );

  const scheduleSave = useCallback(
    (next: T) => {
      if (readOnly) return;
      clearTimer();
      pendingRef.current = next;
      timerRef.current = setTimeout(() => void persist(next), AUTOSAVE_MS);
    },
    [persist, readOnly],
  );

  const update = useCallback(
    (updater: (c: T) => T) => {
      setContent((prev) => {
        const next = updater(prev);
        contentRef.current = next;
        scheduleSave(next);
        return next;
      });
    },
    [scheduleSave],
  );

  const saveNow = useCallback(async (): Promise<boolean> => {
    clearTimer();
    if (readOnly) return true;
    return persist(contentRef.current);
  }, [persist, readOnly]);

  return { content, update, saveNow, saveState, saveErrorMessage };
}
